import { buildPermissionsTree, getPlainArray } from "Common/permissions";
import Button from "Components/button";
import { TextCell, clearSelection } from "Components/dataGridBase";
import ProgressOverlay from "Components/progressOverlay";
import WelcomePanel, { emptyContentIcon } from "Components/welcomePanel";
import YesNoConfirmation from "Components/yesNoConfirmation";
import classNames from "classnames";
import { Column, Editing, Form, Toolbar, Item as ToolbarItem } from "devextreme-react/data-grid";
import { ButtonItem, Item } from "devextreme-react/form";
import TreeView from "devextreme-react/tree-view";
import { RequiredRule } from "devextreme-react/validator";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import omit from "lodash/omit";
import sum from "lodash/sum";
import xor from "lodash/xor";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NEW_ROLE, ROLE_SERVER_IGNORED_FIELDS } from "./constants";
import locale from "./roles.locale";
import Styled, { SIZES } from "./roles.styled";
const defaultEditorOption = {
    showClearButton: true,
    placeholder: "",
    height: 44
};
const formElementAttribute = { style: `min-height: ${SIZES.MIN_FORM_HEIGHT}px;` };
const getPlainTree = (tree) => {
    const result = [];
    getPlainArray(tree, result);
    return result;
};
const filterBuilderFields = [
    {
        dataField: "Name",
        dataType: "string",
        caption: locale.NAME
    },
    {
        dataField: "CountPermissions",
        dataType: "number",
        caption: locale.COUNT_PERMISSIONS
    },
    {
        dataField: "Permissions",
        dataType: "number",
        caption: locale.PERMISSIONS
    },
    {
        dataField: "Description",
        dataType: "string",
        caption: locale.DESCRIPTION
    }
];
export default function Roles(props) {
    var _a;
    const gridRef = useRef(null);
    const [selected, setSelected] = useState([]);
    const formData = useRef({ ...NEW_ROLE });
    const [newRoleEditorMode, setNewRoleEditorMode] = useState(true);
    const [tree, setTree] = useState(() => { var _a; return buildPermissionsTree((_a = formData.current) === null || _a === void 0 ? void 0 : _a.CheckedPermissions, props.expandedPermissions); });
    useEffect(() => {
        !isEmpty(selected) && setSelected([]);
        clearSelection(gridRef.current);
        props.onInitialized && props.onInitialized();
    }, [props.onInitialized]);
    const handleDeleted = useCallback(() => {
        !isEmpty(selected) && props.onDeleted && props.onDeleted(map(selected, "Id"));
    }, [selected]);
    const resetFormTemporaryData = useCallback((role) => {
        formData.current = role ? { ...role } : { ...NEW_ROLE };
        const prevChecked = map(filter(getPlainTree(tree), e => e.selected && Number.isFinite(Number.parseInt(e.id))), "id");
        if (!isEmpty(xor(prevChecked, formData.current.CheckedPermissions))) {
            const newTree = buildPermissionsTree(formData.current.CheckedPermissions, props.expandedPermissions);
            setTree(newTree);
        }
    }, [tree]);
    const handleCloseEditor = useCallback(() => {
        var _a;
        const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
        setTimeout(() => {
            instance && instance.cancelEditData();
        }, 0);
        resetFormTemporaryData();
    }, [resetFormTemporaryData]);
    const handleRowClick = useCallback((e) => {
        var _a;
        setNewRoleEditorMode(false);
        resetFormTemporaryData(e.data);
        const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
        instance && instance.editRow(e.rowIndex);
    }, []);
    const handleCollapsedExpanded = useCallback(() => {
        const expandedPermissions = map(filter(getPlainTree(tree), "expanded"), "id");
        props.onChangeExpandedPermissions && props.onChangeExpandedPermissions(expandedPermissions);
    }, [props.onChangeExpandedPermissions, tree]);
    const handleTreeItemClick = useCallback((e) => {
        if (!e.itemData)
            return;
        if (e.itemData.expanded)
            e.component.collapseItem(e.itemData);
        else
            e.component.expandItem(e.itemData);
    }, []);
    const userFormItems = useMemo(() => [
        React.createElement(Item, { key: 0, itemType: "group", cssClass: "with-role-form-caption-height", colSpan: 2, colCount: 5, horizontalAlignment: "left" },
            React.createElement(Item, { colSpan: 4, horizontalAlignment: "left", cssClass: "form-group-content-hidden", itemType: "group", caption: newRoleEditorMode ? locale.CREATING : locale.EDITING }),
            React.createElement(ButtonItem, { buttonOptions: {
                    icon: "close",
                    elementAttr: { class: "no-default-colors no-border animate-hover-color" },
                    height: SIZES.FORM_CAPTION_HEIGHT,
                    width: SIZES.FORM_CAPTION_HEIGHT,
                    onClick: handleCloseEditor
                }, cssClass: "form-group-content-hidden d-flex justify-content-flex-end v-center-items with-role-form-caption-height" })),
        React.createElement(Item, { key: 1, itemType: "group", horizontalAlignment: "left" },
            React.createElement(Item, { dataField: "Name", editorOptions: defaultEditorOption },
                React.createElement(RequiredRule, { message: locale.NAME_REQUIRED })),
            React.createElement(Item, { dataField: "Description", editorOptions: defaultEditorOption })),
        React.createElement(Item, { key: 2, itemType: "group", horizontalAlignment: "right" },
            React.createElement(Item, { dataField: "Permissions" }))
    ], [newRoleEditorMode, tree]);
    const handleRowUpdating = useCallback((e) => {
        const resultData = {
            ...e.oldData,
            ...e.newData
        };
        props.onSaveRole &&
            props.onSaveRole(omit(resultData, ROLE_SERVER_IGNORED_FIELDS), handleCloseEditor);
        resetFormTemporaryData();
        e.cancel = true;
    }, [props.data, props.onSaveRole, handleCloseEditor]);
    const handleRowInserting = useCallback((e) => {
        const resultData = { ...NEW_ROLE, ...e.data };
        props.onSaveRole &&
            props.onSaveRole(omit(resultData, ROLE_SERVER_IGNORED_FIELDS), handleCloseEditor);
        resetFormTemporaryData();
        e.cancel = true;
    }, [props.onSaveRole, handleCloseEditor]);
    const toolbar = useMemo(() => (React.createElement(Toolbar, null,
        React.createElement(ToolbarItem, { name: "searchPanel", location: "before", cssClass: "datagrid-toolbar-search-panel" }),
        props.canDelete && (React.createElement(ToolbarItem, { location: "after", component: () => (React.createElement("div", { className: "d-flex with-table-toolbar-gap justify-content-center" },
                React.createElement(YesNoConfirmation, { title: locale.getConfirmDeleteText(selected.length), onConfirm: handleDeleted, disabled: !selected.length },
                    React.createElement(Button, { disabled: !selected.length, className: "btn-text-red btn-h-m" },
                        React.createElement("i", { className: "icon-trash pr-0_75" }),
                        locale.DELETE)))) })),
        props.canEdit && (React.createElement(ToolbarItem, { widget: "dxButton", options: {
                icon: "add",
                elementAttr: { class: "btn-primary btn-with-icon btn-h-m" },
                text: locale.CREATE,
                onClick: () => {
                    var _a;
                    setNewRoleEditorMode(true);
                    const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
                    resetFormTemporaryData();
                    instance && instance.addRow();
                }
            } })))), [resetFormTemporaryData, props.canDelete, props.canEdit, selected.length]);
    const handleEditPermissionsCellRender = useCallback((cellInfo) => {
        const setEditedValue = (e) => {
            const selectedNodeKeys = e.component.getSelectedNodeKeys();
            const selectedPerms = filter(map(selectedNodeKeys, key => Number.parseInt(key)), id => !Number.isNaN(id));
            const permissions = sum(selectedPerms);
            cellInfo.setValue && cellInfo.setValue(permissions);
        };
        return (React.createElement(TreeView, { className: classNames(!props.canEdit && "checkbox-disabled"), selectNodesRecursive: true, showCheckBoxesMode: "normal", selectByClick: false, selectionMode: "multiple", items: tree, width: "100%", onItemCollapsed: handleCollapsedExpanded, onItemExpanded: handleCollapsedExpanded, onItemSelectionChanged: setEditedValue, onItemClick: handleTreeItemClick }));
    }, [tree, handleCollapsedExpanded, handleTreeItemClick]);
    const gridComponent = useMemo(() => (React.createElement(Styled.DataGridBase, { gridRef: gridRef, key: "roles", data: props.data, onRowClick: props.canEdit ? handleRowClick : undefined, onSetSelected: props.canDelete ? setSelected : undefined, onRowUpdating: props.canEdit ? handleRowUpdating : undefined, onRowInserting: props.canEdit ? handleRowInserting : undefined, searchPanelOn: true, filterBuilderFields: filterBuilderFields },
        toolbar,
        props.canEdit && (React.createElement(Editing, { mode: "form", allowAdding: true },
            React.createElement(Form, { labelLocation: "top", elementAttr: formElementAttribute, labelMode: "floating", colCount: 2, showColonAfterLabel: false, focusStateEnabled: false }, userFormItems))),
        React.createElement(Column, { dataField: "Name", dataType: "string", caption: locale.NAME, cellRender: TextCell, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "CountPermissions", dataType: "number", alignment: "left", caption: locale.COUNT_PERMISSIONS, cellRender: TextCell, width: 145, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "Permissions", dataType: "number", alignment: "left", caption: locale.PERMISSIONS, cellRender: TextCell, editCellRender: handleEditPermissionsCellRender, visible: false, width: 145, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "Description", dataType: "string", caption: locale.DESCRIPTION, cellRender: TextCell, width: "55%", editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }))), [
        props.data,
        userFormItems,
        handleRowUpdating,
        handleRowInserting,
        props.canDelete,
        props.canEdit,
        handleEditPermissionsCellRender,
        selected.length
    ]);
    return (React.createElement("div", { className: classNames("panel-body-padding stretched-to-fit", props.className) },
        React.createElement(ProgressOverlay, { show: props.inProgress },
            React.createElement("div", { className: "flex-single-column with-grid-16" },
                React.createElement("div", { className: "grid-cols-auto-1fr" },
                    React.createElement("h2", { className: "font-bold" }, locale.ROLES)),
                React.createElement("div", { className: "flex-greedy-block" }, ((_a = props.data) === null || _a === void 0 ? void 0 : _a.length) === 0 ? (React.createElement(WelcomePanel, { message: locale.NO_DATA_TO_SHOW, icon: emptyContentIcon })) : (gridComponent))))));
}
